import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Autocomplete,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import CategoryService, { Category } from '../../services/category.service';
import { NumberTextField } from '../../components/common';

const CategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState<any>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [existingImage, setExistingImage] = useState<any>(null);

  const [formData, setFormData] = useState<Category>({
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0,
  });

  useEffect(() => {
    fetchCategories();
    if (isEdit && id) {
      fetchCategory(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await CategoryService.getAll({
        populate: 'parentCategory',
        'pagination[pageSize]': 100,
      });
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchCategory = async (categoryId: number) => {
    try {
      setLoading(true);
      const response = await CategoryService.getOne(categoryId);
      const categoryData = response.data?.data;
      
      if (categoryData) {
        const attrs = categoryData.attributes;
        setFormData({
          name: attrs.name,
          description: attrs.description || '',
          isActive: attrs.isActive !== false,
          sortOrder: attrs.sortOrder || 0,
          parentCategory: attrs.parentCategory?.data?.id,
        });

        // Set parent category for autocomplete
        if (attrs.parentCategory?.data) {
          setSelectedParent(attrs.parentCategory.data);
        }

        // Set existing image
        if (attrs.image?.data) {
          setExistingImage(attrs.image.data);
        }
      }
    } catch (error) {
      console.error('Error fetching category:', error);
      setError('Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleParentChange = (event: any, newValue: any) => {
    setSelectedParent(newValue);
    setFormData((prev) => ({
      ...prev,
      parentCategory: newValue?.id,
    }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear existing image when new one is selected
      setExistingImage(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setExistingImage(null);
  };

  const uploadImage = async (): Promise<number | null> => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append('files', imageFile);
    const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');

    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data && data[0]) {
        return data[0].id;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Upload image if new one selected
      let imageId: number | null = null;
      if (imageFile) {
        imageId = await uploadImage();
      } else if (existingImage) {
        imageId = existingImage.id;
      }

      const submitData = {
        ...formData,
        image: imageId || undefined,
      };

      if (isEdit && id) {
        await CategoryService.update(parseInt(id), submitData);
        setSuccess('Category updated successfully!');
      } else {
        await CategoryService.create(submitData);
        setSuccess('Category created successfully!');
      }

      setTimeout(() => {
        navigate('/categories');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  // Filter out current category from parent options to prevent circular reference
  const parentOptions = categories.filter((cat) => cat.id !== parseInt(id || '0'));

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {isEdit ? 'Edit Category' : 'Create Category'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <TextField
                fullWidth
                required
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                helperText="Will be displayed in the product catalog"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of this category..."
              />

              <Autocomplete
                value={selectedParent}
                onChange={handleParentChange}
                options={parentOptions}
                loading={categoriesLoading}
                getOptionLabel={(option) => option.attributes?.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Category"
                    helperText="Optional: Select a parent category to create a sub-category"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoriesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <NumberTextField
                fullWidth
                label="Sort Order"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleChange}
                inputProps={{ min: 0 }}
                allowDecimal={false}
                helperText="Lower numbers appear first"
              />
            </Stack>
          </Paper>

          {/* Image Upload */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Category Image
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </Button>

              {/* Existing Image */}
              {existingImage && !imagePreview && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Image
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={`${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${existingImage.attributes?.url}`}
                      alt="Category"
                      variant="rounded"
                      sx={{ width: 150, height: 150 }}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={removeImage}
                      sx={{ mt: 1 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              )}

              {/* New Image Preview */}
              {imagePreview && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    New Image Preview
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={imagePreview}
                      alt="Preview"
                      variant="rounded"
                      sx={{ width: 150, height: 150 }}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={removeImage}
                      sx={{ mt: 1 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Status */}
          <Paper sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive !== false}
                  onChange={handleChange}
                />
              }
              label="Category is Active"
            />
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/categories')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Category' : 'Create Category'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default CategoryForm;

